import { Alert, Badge, Button, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';

import { useExportPageReportDialogs } from '../api/exportDialogs';
import { aiReportTypes } from '../types';
import {
  GEO_LABEL,
  KIND_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  etaLabel,
  formatCost,
  formatDateTime,
  formatUtcDate,
  printReport,
  reportToMarkdown,
  toIsoDayUtc,
} from '../utils/reportDocument';

import { ResultSections } from './ResultSections';

const STATUS_COLOR: Record<aiReportTypes.Status, string> = {
  queued: 'gray',
  running: 'blue',
  done: 'teal',
  empty: 'yellow',
  failed: 'red',
};

type Props = {
  report: aiReportTypes.Report;
  batch: aiReportTypes.BatchItem[];
};

export const ReportCard = ({ report, batch }: Props) => {
  const summary = batch.find((item) => item.type === 'summary');
  const eta = etaLabel(report);
  const isReady = report.status === 'done' && Boolean(report.result);

  const exportDialogs = useExportPageReportDialogs();
  const canDownloadDialogs = report.type === 'page' && Boolean(report.pageId);

  const downloadDialogs = () => {
    if (!report.pageId) return;
    exportDialogs.mutate({
      geo: report.geo,
      source: report.source,
      pageIds: [report.pageId],
      periodFrom: toIsoDayUtc(report.periodFrom),
      periodTo: toIsoDayUtc(report.periodTo),
    });
  };

  const copyMarkdown = async () => {
    await navigator.clipboard.writeText(reportToMarkdown(report));
    notifications.show({ message: 'Markdown скопирован', color: 'teal' });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>{report.pageName}</Title>
        <Badge color={STATUS_COLOR[report.status]} variant="light">
          {STATUS_LABEL[report.status]}
        </Badge>
      </Group>
      {eta && (
        <Text c="dimmed" size="sm">
          {eta}. Статус обновляется без перезагрузки.
        </Text>
      )}
      {(report.status === 'failed' || report.status === 'empty') && report.error && (
        <Alert color={report.status === 'failed' ? 'red' : 'yellow'}>{report.error}</Alert>
      )}
      <Stack gap={4}>
        <Text>Гео: {GEO_LABEL[report.geo]}</Text>
        <Text>Источник: {SOURCE_LABEL[report.source]}</Text>
        <Text>
          Период: {formatUtcDate(report.periodFrom)} — {formatUtcDate(report.periodTo)} UTC
        </Text>
        <Text>
          Тип: {KIND_LABEL[report.type]}, промт {report.promptKey} v{report.promptVersion}
        </Text>
        <Text>Язык: {report.language.toUpperCase()}</Text>
        <Text>Автор: {report.createdByName}</Text>
        <Text>Создан: {formatDateTime(report.createdAt)}</Text>
        {report.model && <Text>Модель: {report.model}</Text>}
        <Text>Стоимость: {formatCost(report.costUsd)}</Text>
      </Stack>
      {summary && summary._id !== report._id && (
        <Button component={Link} to={`/ai-reports/${summary._id}`} variant="light">
          Сводный отчёт пачки
        </Button>
      )}
      {report.result && <ResultSections result={report.result} />}
      {report.input && (
        <Stack gap={4}>
          <Title order={4}>Входные данные</Title>
          <Text>Чаты: {report.input.chats}</Text>
          <Text>Входящие: {report.input.messagesIn}</Text>
          <Text>Исходящие: {report.input.messagesOut}</Text>
          <Text>Менеджеры: {report.input.managers.join(', ') || '—'}</Text>
        </Stack>
      )}
      <Group>
        <Button disabled={!isReady} onClick={() => printReport(report)}>
          Скачать PDF
        </Button>
        <Button variant="default" disabled={!isReady} onClick={copyMarkdown}>
          Копировать Markdown
        </Button>
        {canDownloadDialogs && (
          <Button variant="default" loading={exportDialogs.isLoading} onClick={downloadDialogs}>
            Скачать диалоги JSON
          </Button>
        )}
      </Group>
    </Stack>
  );
};
